import React, { useEffect, useMemo, useRef, useState } from "react";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  InputBase,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Popper from "@mui/material/Popper";
import MenuControlIcon from "../../assets/images/menu-control.svg";
import MenuControlIconExpand from "../../assets/images/menu-control-expand.svg";
import classnames from "classnames";
import styles from "./index.module.scss";
import { withAuth } from "../AuthWrapper";
import { useTranslation } from "react-i18next";
import { useConfigStore } from "../../store/config";
import { useMenuStore } from "../../store/menu";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { buildSideMenuItems, MenuItem } from "./menuConfig";
import { mapAcModulesToMenuItems } from "../../acMenu/mapAcModulesToMenuItems";

interface NestedMenuProps {
  // menuItems: MenuItem[];
}

const NestedMenu: React.FC<NestedMenuProps> = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [language, matchedAcMenus] = useConfigStore((state) => [
    state.language,
    state.matchedAcMenus,
  ]);
  const [isFilterMenu, setIsFilterMenu] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<string>("");

  const [sideMenuOpen, setSideMenuOpen] = useMenuStore((state) => [
    state.sideMenuOpen,
    state.setSideMenuOpen,
  ]);

  const dynamicMenus: MenuItem[] = useMemo(
    () => mapAcModulesToMenuItems(matchedAcMenus, language),
    [matchedAcMenus, language]
  );

  const allMenus: MenuItem[] = useMemo(
    () => [...buildSideMenuItems(t, props.authList), ...dynamicMenus],
    // Labels follow language; omit `t` (new fn each render from i18n) to keep allMenus stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicMenus, props.authList, language]
  );

  const [expandMenus, setExpandMenus] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [activeMenuID, setActiveMenuID] = useState<string>("");
  const location = useLocation();
  const [railAnchor, setRailAnchor] = useState<null | HTMLElement>(null);
  const [railItem, setRailItem] = useState<MenuItem | null>(null);
  const railCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openRailFlyout = (e: React.MouseEvent<HTMLElement>, item: MenuItem) => {
    setRailAnchor(e.currentTarget);
    setRailItem(item);
  };
  const closeRailFlyout = () => {
    if (railCloseTimer.current) {
      clearTimeout(railCloseTimer.current);
      railCloseTimer.current = null;
    }
    setRailAnchor(null);
    setRailItem(null);
  };
  const scheduleCloseRailFlyout = () => {
    if (railCloseTimer.current) {
      clearTimeout(railCloseTimer.current);
    }
    railCloseTimer.current = setTimeout(() => {
      closeRailFlyout();
    }, 150);
  };
  const cancelCloseRailFlyout = () => {
    if (railCloseTimer.current) {
      clearTimeout(railCloseTimer.current);
      railCloseTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (railCloseTimer.current) {
        clearTimeout(railCloseTimer.current);
      }
    };
  }, []);

  const handleMenuClick = (menuID: string, menu: MenuItem) => {
    if (menu.children && !menu.path) {
      setExpandMenus((prevState) => ({
        ...prevState,
        [menuID]: !prevState[menuID],
      }));
    } else {
      setActiveMenuID(menuID);
    }
    if (menu.path) {
      navigate(menu.path);
    }
  };

  // For refreshing： find par menu ids and activeMenu id
  const findActiveIdAndParentIdByPath = (
    tree: MenuItem[],
    targetPath: string,
    parentIDs: string[] = []
  ): { parentIDs: string[]; currentNodeID: string } => {
    for (const node of tree) {
      if (node.path && targetPath.includes(node.path)) {
        return { parentIDs, currentNodeID: node.menuID };
      }

      if (node?.children?.length) {
        const result = findActiveIdAndParentIdByPath(
          node.children,
          targetPath,
          [...parentIDs, node.menuID]
        );
        if (result.parentIDs.length > 0) {
          return result;
        }
      }
    }
    return { parentIDs: [], currentNodeID: "" };
  };

  // Refresh: activeMenu should highlight, par menus should expand
  useEffect(() => {
    const activeIdAndParentId = findActiveIdAndParentIdByPath(
      allMenus,
      location.pathname
    );
    const parentIDs = activeIdAndParentId.parentIDs;
    setActiveMenuID(activeIdAndParentId.currentNodeID);
    for (let item of parentIDs) {
      setExpandMenus((prevState) => ({
        ...prevState,
        [item]: true,
      }));
    }
  }, [location.pathname, allMenus]);

  // Search keyword filtering returns a menu structure with only keywords
  const filterMenu = (keywords: string, menuList: MenuItem[]): MenuItem[] => {
    return menuList
      .map((menu) => {
        if (menu.children && menu.visible) {
          return {
            ...menu,
            children: filterMenu(keywords, menu.children),
          };
        }
        return menu;
      })
      .filter(
        (menu) =>
          menu.label.toLowerCase().includes(keywords.toLowerCase()) ||
          (menu.children && menu.children.length > 0)
      );
  };

  const renderMenuIcon = (item: MenuItem) => {
    if (item.iconSrc) {
      return (
        <img
          src={item.iconSrc}
          alt=""
          style={{ width: 16, height: 16, objectFit: "contain" }}
        />
      );
    }
    if (typeof item.icon === "string") {
      return <i className={item.icon}></i>;
    }
    return item.icon;
  };

  const renderSubMenu = (parId: string, children: MenuItem[]) => {
    if (!children) return null;
    return (
      <Collapse in={expandMenus[parId]} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className="sub-menu">
          {children.map((subitem) => {
            if (subitem.visible) {
              return (
                <ListItemButton
                  key={subitem.menuID}
                  sx={{
                    pl: 4,
                  }}
                  // component={subitem.path ? Link : "div"}
                  // to={subitem.path}
                  className={classnames({
                    active: activeMenuID === subitem?.menuID,
                  })}
                  onClick={() => handleMenuClick(subitem.menuID, subitem)}
                >
                  {/* <ListItemIcon>{subitem.icon}</ListItemIcon> */}
                  <ListItemText primary={subitem.label} />
                </ListItemButton>
              );
            } else {
              return <></>;
            }
          })}
        </List>
      </Collapse>
    );
  };

  const renderMenu = (menuList: MenuItem[]) => {
    return (
      <List>
        {menuList.map((item) => {
          const activeFlag = activeMenuID === item.menuID;
          if (item.visible) {
            return (
              <Box key={item.menuID}>
                <ListItemButton
                  onClick={() => handleMenuClick(item.menuID, item)}
                  className={activeFlag ? "active" : ""}
                // component={item.path ? Link : "div"}
                // to={item.path}
                >
                  <ListItemIcon>{renderMenuIcon(item)}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {(item.children?.length && (
                    <KeyboardArrowDownIcon
                      style={{
                        transform: expandMenus[item.menuID]
                          ? "rotate(-180deg)"
                          : "rotate(0)",
                      }}
                    />
                  )) ||
                    null}
                </ListItemButton>
                {item.children && renderSubMenu(item.menuID, item.children)}
              </Box>
            );
          } else {
            return <></>;
          }
        })}
      </List>
    );
  };

  const renderRail = (menuList: MenuItem[]) => (
    <List>
      {menuList
        .filter((item) => item.visible)
        .map((item) => {
          const activeFlag = activeMenuID === item.menuID;
          return (
            <Box
              key={item.menuID}
              data-testid={`rail-item-${item.menuID}`}
              onMouseEnter={(e) => {
                cancelCloseRailFlyout();
                openRailFlyout(e, item);
              }}
              onMouseLeave={scheduleCloseRailFlyout}
            >
              <ListItemButton
                className={activeFlag ? "active" : ""}
                onClick={() => item.path && handleMenuClick(item.menuID, item)}
              >
                <ListItemIcon>{renderMenuIcon(item)}</ListItemIcon>
              </ListItemButton>
            </Box>
          );
        })}

      <Popper
        open={Boolean(railAnchor && railItem)}
        anchorEl={railAnchor}
        placement="right-start"
        style={{ zIndex: 1001 }}
      >
        <Box
          className="rail-flyout"
          data-testid="rail-flyout"
          onMouseEnter={cancelCloseRailFlyout}
          onMouseLeave={scheduleCloseRailFlyout}
        >
          {railItem?.children?.length ? (
            <List disablePadding>
              {railItem.children
                .filter((c) => c.visible)
                .map((c) => (
                  <ListItemButton
                    key={c.menuID}
                    className={activeMenuID === c.menuID ? "active" : ""}
                    onClick={() => {
                      handleMenuClick(c.menuID, c);
                      closeRailFlyout();
                    }}
                  >
                    <ListItemText primary={c.label} />
                  </ListItemButton>
                ))}
            </List>
          ) : (
            <List disablePadding>
              <ListItemButton
                className={activeMenuID === railItem?.menuID ? "active" : ""}
                onClick={() => {
                  railItem && handleMenuClick(railItem.menuID, railItem);
                  closeRailFlyout();
                }}
              >
                <ListItemText primary={railItem?.label} />
              </ListItemButton>
            </List>
          )}
        </Box>
      </Popper>
    </List>
  );

  const menuMap = useMemo(() => {
    return filterMenu(keywords, allMenus);
  }, [keywords, language, allMenus]);

  return (
    <Box
      className={classnames(styles["side-menu"], {
        [styles.collapsed]: !sideMenuOpen,
      })}
    >
      <Box className="search-input m-b-8">
        {!sideMenuOpen ? (
          <Stack
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <IconButton
              type="button"
              data-testid="side-menu-toggle"
              aria-label={t("menu.showMenu")}
              onClick={() => setSideMenuOpen(true)}
              className="toggle-icon"
            >
              <img
                src={MenuControlIconExpand}
                alt="expand menu"
                width={16}
                height={16}
              />
            </IconButton>
          </Stack>
        ) : isFilterMenu ? (
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder={t(`menu.${"searchModule"}`)}
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
            <IconButton
              type="button"
              onClick={() => {
                setIsFilterMenu(!isFilterMenu);
                setKeywords("");
              }}
            >
              <CloseIcon></CloseIcon>
            </IconButton>
            <IconButton
              type="button"
              data-testid="side-menu-toggle"
              aria-label={t("menu.hideSideMenu")}
              onClick={() => setSideMenuOpen(false)}
              className="toggle-icon"
            >
              <img
                src={MenuControlIcon}
                alt="collapse menu"
                width={16}
                height={16}
              />
            </IconButton>
          </Stack>
        ) : (
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography variant="h6">{t("menu.modules")}</Typography>
            <IconButton
              type="button"
              onClick={() => setIsFilterMenu(!isFilterMenu)}
            >
              <FilterListIcon color="inherit"></FilterListIcon>
            </IconButton>
            <IconButton
              type="button"
              data-testid="side-menu-toggle"
              aria-label={t("menu.hideSideMenu")}
              onClick={() => setSideMenuOpen(false)}
              className="toggle-icon"
            >
              <img
                src={MenuControlIcon}
                alt="collapse menu"
                width={16}
                height={16}
              />
            </IconButton>
          </Stack>
        )}
      </Box>
      {sideMenuOpen ? renderMenu(menuMap) : renderRail(menuMap)}
    </Box>
  );
};

export default withAuth(NestedMenu);
